import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { UserService } from '../../services/user.service';
import { ContractService } from '../../services/contract.service';

import { EsignNoteInfoListItem } from 'src/app/models/contract';
import { NoteDetailTypes } from '../note-detail/note-detail.component';

@Component({
  selector: 'app-esign-details',
  templateUrl: './esign-details.component.html',
  styleUrls: ['./esign-details.component.scss']
})
export class EsignDetailsComponent implements OnInit, AfterViewInit {
  // Have to use the following ElementDisplayStyle variables and ngStyle to show/hide table elements
  // This because the table paginator will not attach correctly otherwise - this is a work around for a known issue
  private _hideElementDisplayStyle = 'none';
  private _showElementDisplayStyle = 'inline';
  private _showElementDisplayStyleIE = 'inline-block';

  private _handleError: HandleError;
  private _logging = environment.logging;

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  userFullName = '';
  participantIdString = '';
  participantId: number = null;
  esignNoteInfoList: EsignNoteInfoListItem[] = [];

  selectedNoteId: number = null;
  selectedRelationshipType: string = null;
  noteDetailTypes = NoteDetailTypes;

  showSpinner = false;

  // DataTable
  tableDisplayedColumns = ['noteId', 'noteDate', 'originalNoteAmount', 'schoolName', 'noteStatus', 'options'];
  tableDataSource = new MatTableDataSource<EsignNoteInfoListItem>(this.esignNoteInfoList);
  tableDisplayStyle: string = this._hideElementDisplayStyle;
  tableDisplayStyleIE: string = this._hideElementDisplayStyle;
  esignNoteInfoFound = false;

  filteredNotesList = [...this.esignNoteInfoList];
  selectedNoteStatus = '';
  uniqueNoteStatuses: string[] = [];

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _errorHandler: ErrorHandler,
    private _userService: UserService,
    private _contractService: ContractService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('EsignDetailsComponent');
    this.userFullName = this._userService.getFullName();
    this.participantId = this._userService.getParticipantId();
    this.participantIdString = this.participantId.toString();
    this._userService.clearNoteId();
    this._userService.clearRelationshipType();
    this.getData();
    this.scrollToTop();
  }

  ngOnInit() {
    this.esignNoteInfoFound = true;
    this.tableDisplayStyle = this.esignNoteInfoFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
    this.tableDisplayStyleIE = this.esignNoteInfoFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
  }

  ngAfterViewInit() {}

  getData(): void {
    this.showSpinner = true;
    this.tableDisplayStyle = this._hideElementDisplayStyle;
    this.tableDisplayStyleIE = this._hideElementDisplayStyle;

    this.getEsignNoteInfoList(this.participantId);
  }

  getEsignNoteInfoList(participantId: number): void {
    this.showSpinner = true;
    this._contractService.getEsignNoteInfoList(participantId).subscribe(
      resultArray => {
        this.esignNoteInfoList = resultArray;
        this.filteredNotesList = [...this.esignNoteInfoList];
        this.uniqueNoteStatuses = [...new Set(this.esignNoteInfoList.map(note => note.noteStatus))];

        if (this._logging.serviceCallTraceLog) {
          console.log('EsignDetailsComponent.getEsignNoteInfoList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('EsignDetailsComponent.getEsignNoteInfoList :: result = ' + JSON.stringify(resultArray));
        }

        this.tableDataSource = new MatTableDataSource<EsignNoteInfoListItem>(this.esignNoteInfoList);
        this.esignNoteInfoFound = resultArray.length > 0;
        this.tableDisplayStyle = this.esignNoteInfoFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
        this.tableDisplayStyleIE = this.esignNoteInfoFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
        this.getDataComplete();
      },
      error => this._handleError('getEsignNoteInfoList', error)
    );
  }

  filterNotes() {

    this.selectedNoteId = null;

    if (this.selectedNoteStatus) {
      this.filteredNotesList = this.esignNoteInfoList.filter(
        note => note.noteStatus === this.selectedNoteStatus
      );
    } else {
      this.filteredNotesList = [...this.esignNoteInfoList];
    }
  }

  getDataComplete(): void {
    this.showSpinner = false;
  }

  isGroupHeader(index, item): boolean {
    return item.isGroupHeader;
  }

  selectNote(noteId: number, relationshipType: string) {
    if (this._userService.isLoggedIn()) {
      this.selectedNoteId = noteId;
      this.selectedRelationshipType = relationshipType;
      setTimeout(() => {
        const el = document.getElementById('noteDetails');
        el.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }

  selectCardNote(noteId: number, relationshipType: string) {
    if (this._userService.isLoggedIn()) {
      this.selectedNoteId = noteId;
      this.selectedRelationshipType = relationshipType;
    }
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }

  openEsign() {
    if (this._userService.isLoggedIn()) {
      if (this._logging.navigationTraceLog) {
        console.log('EsignDetailsComponent.openEsign');
      }

      this._userService.setNoteId(this.selectedNoteId);
      this._userService.setRelationshipType(this.selectedRelationshipType);
      this._router.navigate(['esignnote']);
    }
  }
}
